import {
  Box,
  DialogActions,
  DialogContent,
  FormControl,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@material-ui/core";
import { CustomButton } from "componentsNew/buttons/CustomButton";
import { Dispatch, FC, SetStateAction, useEffect, useState } from "react";
import { useModalStyles } from "../styles";
import { useDispatch } from "react-redux";
import { closeModal } from "actions/modalActions";
import { ScheduleReport } from "types/Schedule";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import { UserData } from "types/User";
import { ScheduleRoleSelect } from "componentsNew/elements/SelectField";
import { daScheduleRoleKeyToId, scheduleRoleDa } from "mocks/roleDA";
import { inputNumberValidateOnPaste } from "utils/formats";
import { OnChangeEvent } from "types/general";
import {
  deleteScheduleReportAction,
  scheduleCreateUpdateReport,
} from "actions/scheduleActions";
import { getHelperOfUserData } from "utils/scheduleHelpers";
import { useTypeSelectors } from "hooks/useTypeSelectors";
import { DRIVER } from "constants/constants";

interface SendNotificationModalContentProps {
  handleSubmitSMS: any;
}

interface EditScheduleModalContentProps {
  date: string | undefined;
  user: UserData;
  schedule: ScheduleReport | undefined;
  deleteStep: boolean;
  setDeleteStep: Dispatch<SetStateAction<boolean>>;
  onRoleChange?: (role: number) => void;
}

export const EditScheduleModalContent: FC<EditScheduleModalContentProps> = ({
  date,
  user,
  schedule,
  deleteStep,
  setDeleteStep,
  onRoleChange,
}) => {
  const classes = useModalStyles();
  const dispatch = useDispatch();
  const onClose = () => dispatch(closeModal());
  
  const [filteredScheduleList, setFilteredScheduleList] = useState<ScheduleReport[] | []>([]);
  const [isAvailable, setIsAvailable] = useState<boolean>(schedule?.is_available || false);
  const [role, setRole] = useState<number>(schedule?.role || 0);
  const [startTime, setStartTime] = useState<string>(schedule?.shift_start_time || "09:40");
  const fullRole = scheduleRoleDa.find((el) => el.value === role) || null;
  const [roleHours, setRoleHours] = useState<number | null>(
    schedule?.role_hours || fullRole?.baseHours || null,
  );
  const [helperOf, setHelperOf] = useState<number | null>(schedule?.helper_of || null);
  const { userList, dataList, scheduleFilters } = useTypeSelectors((state) => state.scheduleTable);

  useEffect(() => {
    if ((role || role === 0) && role !== 13 && helperOf) {
      setHelperOf(null);
    }
  }, [role, helperOf, roleHours]);

  useEffect(() => {
    if ((!schedule && Number(scheduleFilters.station) === 19) || (schedule?.role === null && Number(scheduleFilters.station) === 19)) {
      setRoleHours(9)
    }
  }, [schedule, scheduleFilters.station]);

  useEffect(() => {
    setFilteredScheduleList(dataList?.filter((report) => report.schedule_date === date));
  }, [dataList, date]);

  const handleSubmit = () => {
    dispatch(
      scheduleCreateUpdateReport(date, user.id, helperOf, role, roleHours, isAvailable, startTime),
    );
    onClose();
  };

  const changeRoleVal = (role: number) => {
    const nextRole = Number(role);
    setRole(nextRole);
    onRoleChange?.(nextRole);
    const selectedRole = scheduleRoleDa.find((el) => el.value === nextRole);
    if (selectedRole?.roleScheduleId ===  daScheduleRoleKeyToId.driver && Number(scheduleFilters.station) === 19) {
      setRoleHours(9);
    }
    else if (selectedRole && !selectedRole.flexible) {
      setRoleHours(selectedRole.baseHours);
    }
  };

  const handleSubmitDelete = () => {
    dispatch(
      deleteScheduleReportAction(schedule?.id, schedule?.schedule_date, schedule?.assigned_to),
    );
    onClose();
  };

  const getHelperOfInfo = (id: number) => {
    const user = getHelperOfUserData(userList, id);

    return <MenuItem value={id}>{`${user?.first_name} ${user?.last_name}`}</MenuItem>;
  };

  const handleResetSteps = () => {
    setDeleteStep(false)
    onClose();
  };

  return (
    <>
      <DialogContent className={classes.dialogContent}>
        <Box style={{ display: "flex", gap: 32, alignItems: "center", marginBottom: 16 }}>
          <Box style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <CalendarTodayOutlinedIcon style={{ color: "#5F6164" }} />
            <Typography>{date}</Typography>
          </Box>
          <Box style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <PersonOutlinedIcon style={{ color: "#5F6164" }} />
            <Typography>
              {user.first_name} {user.last_name}
            </Typography>
          </Box>
        </Box>
        {!deleteStep ? (
          <>
            <Box className={classes.horizontalBox}>
              <Typography style={{ fontWeight: 500 }}>Available</Typography>
              <Switch checked={isAvailable} onChange={() => setIsAvailable(!isAvailable)} />
            </Box>
            {isAvailable ? (
              <Box style={{ display: "flex", flexDirection: "column", gap: 24 }}>
                <ScheduleRoleSelect
                  value={role}
                  onChange={changeRoleVal}
                  fullWidth
                  scheduleUserRole={user.role}
                />
                {![
                  daScheduleRoleKeyToId.clientCancelled,
                  daScheduleRoleKeyToId.mycuCancelled,
                  daScheduleRoleKeyToId.rejected,
                  daScheduleRoleKeyToId.absent,
                ].includes(role) && (
                  <>
                    <FormControl fullWidth>
                      <Typography className={classes.textLabel}>Start Time</Typography>
                      <TextField
                        type="time"
                        variant="outlined"
                        defaultValue="09:40"
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                      />
                    </FormControl>
                    <FormControl fullWidth>
                      <Typography className={classes.textLabel}>Hours</Typography>
                      <TextField
                        type="number"
                        variant="outlined"
                        disabled={!fullRole?.flexible}
                        value={roleHours}
                        onChange={(e: OnChangeEvent) => setRoleHours(Number(e.target.value))}
                        onPaste={inputNumberValidateOnPaste}
                      />
                    </FormControl>
                    {role === daScheduleRoleKeyToId.helper && (
                      <FormControl fullWidth>
                        <Typography className={classes.textLabel}>Helper Of</Typography>
                        <Select
                          variant="outlined"
                          label="Helper Of"
                          value={helperOf}
                          onChange={(e: OnChangeEvent) => setHelperOf(e.target.value)}
                        >
                          {helperOf && getHelperOfInfo(helperOf)}
                          {userList
                            .filter((u) => u.id !== user.id)
                            .filter(
                              (user) =>
                                user.role === DRIVER &&
                                filteredScheduleList.some(
                                  (report) =>
                                    report.assigned_to === user.id &&
                                    [0, 8].includes(report?.role) &&
                                    report.schedule_date,
                                ),
                            )
                            .filter(
                              (user) =>
                                !filteredScheduleList.some(
                                  (report) => report?.helper_of === user.id,
                                ),
                            )
                            .map((el: UserData) => (
                              <MenuItem key={el.id} value={el.id}>
                                {el.first_name} {el.last_name}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    )}
                  </>
                )}
              </Box>
            ) : (
              ""
            )}
          </>
        ) : (
          <Typography style={{ fontWeight: 500 }}>
            Are you sure you want to delete this shift?
          </Typography>
        )}
      </DialogContent>
      <DialogActions
        className={classes.dialogActions}
        style={{ display: "flex", justifyContent: "space-between" }}
      >
        {!deleteStep ? (
          <>
            {schedule ? (
              <CustomButton
                buttonText={"Delete"}
                variant="text"
                theme={"red"}
                onClick={() => setDeleteStep(true)}
              />
            ) : (
              <Box></Box>
            )}
            <Box>
              <CustomButton buttonText={"Cancel"} onClick={handleResetSteps} variant="text" />
              <CustomButton
                buttonText={"Save"}
                theme={"blue"}
                onClick={handleSubmit}
                variant="contained"
              />
            </Box>
          </>
        ) : (
          <>
            <CustomButton
              buttonText={"Cancel"}
              onClick={() => setDeleteStep(false)}
              variant="text"
            />
            <CustomButton
              buttonText={"Delete"}
              theme={"red"}
              onClick={handleSubmitDelete}
              variant="contained"
            />
          </>
        )}
      </DialogActions>
    </>
  );
};

export const SendNotificationModalContent: FC<SendNotificationModalContentProps> = ({
  handleSubmitSMS,
}) => {
  const classes = useModalStyles();
  const dispatch = useDispatch();
  const onClose = () => dispatch(closeModal());

  const handleSubmit = () => {
    onClose();
    handleSubmitSMS();
  };

  return (
    <>
      <DialogActions className={classes.dialogActions}>
        <CustomButton buttonText={"Cancel"} onClick={onClose} variant="text" />
        <CustomButton
          buttonText={"Send"}
          theme={"blue"}
          onClick={handleSubmit}
          variant="contained"
        />
      </DialogActions>
    </>
  );
};
